import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { LoaderCircle, Medal, Eye, EyeOff, ChevronDown, PackagePlus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Query } from '../../../../api'

interface Props {
  tenderId: string
  round?: number
}

type PriceMode = 'unit' | 'total'
type AbcClass = 'A' | 'B' | 'C'

const ABC_ROW_CLASSES: Record<AbcClass, string> = {
  A: 'border-l-[3px] border-l-blue-500 bg-blue-50/30',
  B: 'border-l-[3px] border-l-amber-400 bg-amber-50/20',
  C: 'border-l-[3px] border-l-gray-300',
}

const ABC_BADGE_CLASSES: Record<AbcClass, string> = {
  A: 'border-blue-300 bg-blue-100 text-blue-700',
  B: 'border-amber-300 bg-amber-100 text-amber-700',
  C: 'border-gray-300 bg-gray-100 text-gray-500',
}

const getCoverageLevel = (percent: number): string => {
  if (percent >= 80) return 'high'
  if (percent >= 50) return 'medium'
  return 'low'
}

const AiBidComparison: React.FC<Props> = ({ tenderId, round }) => {
  const { t } = useTranslation()
  const [priceMode, setPriceMode] = useState<PriceMode>('unit')
  const [hiddenBidIds, setHiddenBidIds] = useState<Set<string>>(new Set())
  const [fillAverages, setFillAverages] = useState(true)
  const [paretoEnabled, setParetoEnabled] = useState(false)
  const [showTrivialItems, setShowTrivialItems] = useState(false)
  const [showUnpriced, setShowUnpriced] = useState(false)
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set())
  const [cardsVisible, setCardsVisible] = useState(true)
  const [colPositions, setColPositions] = useState<Array<{ left: number; width: number }>>([])
  const cardsRef = useRef<HTMLDivElement>(null)
  const tfootRef = useRef<HTMLTableSectionElement>(null)

  const { data, loading } = useQuery(Query.AI_COMPARISON, {
    variables: { tenderId, round },
    skip: !tenderId,
    fetchPolicy: 'cache-and-network',
  })

  const comparison = data?.aiComparison
  const tenderItems = comparison?.tenderItems || []
  const allBids = comparison?.bids || []
  const bids = allBids.filter((b: any) => !hiddenBidIds.has(b.bidId))

  // Observe cards visibility
  useEffect(() => {
    const el = cardsRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setCardsVisible(entry.isIntersecting),
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [allBids.length])

  // Measure ALL tfoot cell positions (index 0 = "Total" label, 1..N = bid columns)
  const measureColumns = useCallback(() => {
    const tfoot = tfootRef.current
    if (!tfoot) return
    const cells = tfoot.querySelectorAll('td')
    const positions: Array<{ left: number; width: number }> = []
    for (let i = 0; i < cells.length; i++) {
      const rect = cells[i].getBoundingClientRect()
      positions.push({ left: rect.left, width: rect.width })
    }
    setColPositions(positions)
  }, [])

  useEffect(() => {
    if (!cardsVisible) measureColumns()
  }, [cardsVisible, measureColumns])

  // Re-measure on scroll/resize while bar is visible
  useEffect(() => {
    if (cardsVisible) return
    const handler = () => measureColumns()
    window.addEventListener('resize', handler)
    window.addEventListener('scroll', handler, { passive: true })
    return () => {
      window.removeEventListener('resize', handler)
      window.removeEventListener('scroll', handler)
    }
  }, [cardsVisible, measureColumns])

  const toggleBid = (bidId: string) => {
    setHiddenBidIds(prev => {
      const next = new Set(prev)
      if (next.has(bidId)) next.delete(bidId)
      else next.add(bidId)
      return next
    })
  }

  const formatNumber = (value: number | null | undefined) => {
    if (value == null) return '-'
    return value.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // Tender total
  const tenderTotal = useMemo(() =>
    tenderItems.reduce((sum: number, item: any) => sum + (item.totalPrice || 0), 0),
    [tenderItems],
  )

  // Pareto ABC classification
  const paretoClassification = useMemo(() => {
    if (!paretoEnabled) return null
    const sorted = [...tenderItems]
      .map((item: any) => ({ itemId: item.id, totalPrice: item.totalPrice || 0 }))
      .sort((a, b) => b.totalPrice - a.totalPrice)

    const safeTotal = tenderTotal > 0 ? tenderTotal : 1
    let cumulative = 0
    const result: Record<string, { percentOfTotal: number; cumulativePercent: number; abcClass: AbcClass }> = {}

    for (const entry of sorted) {
      const pct = (entry.totalPrice / safeTotal) * 100
      cumulative += pct
      const abcClass: AbcClass = cumulative <= 80 ? 'A' : cumulative <= 95 ? 'B' : 'C'
      result[entry.itemId] = { percentOfTotal: pct, cumulativePercent: cumulative, abcClass }
    }
    return result
  }, [paretoEnabled, tenderItems, tenderTotal])

  // Per-bid totals from coverage groups
  const bidTotals = useMemo(() => {
    const totals: Record<string, number> = {}
    for (const bid of bids) {
      totals[bid.bidId] = bid.totalPrice || 0
    }
    return totals
  }, [bids])

  const cheapestBidId = useMemo(() => {
    let min = Infinity
    let id = ''
    for (const [bidId, total] of Object.entries(bidTotals)) {
      if (total > 0 && total < min) { min = total; id = bidId }
    }
    return id
  }, [bidTotals])

  // Build a lookup: for each tender item, get each bid's price info
  const rowAnalysis = useMemo(() => {
    const renderedGroups = new Set<string>()
    return tenderItems.map((item: any) => {
      const bidCells: Record<string, {
        price: number | null
        totalPrice: number | null
        isIncluded: boolean
        isFilled: boolean
        groupType: string | null
      }> = {}

      for (const bid of bids) {
        const group = (bid.coverageGroups || []).find(
          (g: any) => (g.tenderItems || []).some((ti: any) => ti.id === item.id),
        )
        if (!group) {
          bidCells[bid.bidId] = { price: null, totalPrice: null, isIncluded: false, isFilled: false, groupType: null }
          continue
        }

        const bidItem = group.bidItems?.[0]
        if (group.groupType === 'MANY_TO_ONE' && renderedGroups.has(group.id)) {
          bidCells[bid.bidId] = { price: null, totalPrice: null, isIncluded: true, isFilled: false, groupType: 'MANY_TO_ONE' }
        } else {
          if (group.groupType === 'MANY_TO_ONE') renderedGroups.add(group.id)
          const price = priceMode === 'unit'
            ? (bidItem?.unitPrice ?? null)
            : (bidItem?.totalPrice ?? null)
          bidCells[bid.bidId] = { price, totalPrice: bidItem?.totalPrice ?? null, isIncluded: false, isFilled: false, groupType: group.groupType }
        }
      }

      // Compute average and cheapest across bids with real prices only
      const prices = Object.entries(bidCells)
        .filter(([, c]) => c.price != null && !c.isIncluded)
        .map(([bidId, c]) => ({ bidId, price: c.price! }))

      const avgPrice = prices.length > 0
        ? prices.reduce((s, p) => s + p.price, 0) / prices.length
        : null

      const cheapestInRow = prices.length > 0
        ? prices.reduce((min, p) => p.price < min.price ? p : min, prices[0]).bidId
        : null

      // Fill missing prices with average when toggle is on
      if (fillAverages && avgPrice != null) {
        for (const [bidId, cell] of Object.entries(bidCells)) {
          if (cell.price == null && !cell.isIncluded) {
            bidCells[bidId] = { ...cell, price: avgPrice, isFilled: true }
          }
          if (cell.isIncluded) {
            bidCells[bidId] = { ...cell, price: avgPrice, isIncluded: false, isFilled: true }
          }
        }
      }

      const hasAnyPrice = prices.length > 0
      return { item, bidCells, avgPrice, cheapestInRow, hasAnyPrice }
    })
  }, [tenderItems, bids, priceMode, fillAverages])

  const unpricedItemCount = rowAnalysis.filter(r => !r.hasAnyPrice && (r.item.rowType || 'ITEM') === 'ITEM').length

  // All rows in document order, with optional filtering
  const { visibleRows, trivialRows } = useMemo(() => {
    let rows = rowAnalysis

    // Filter out unpriced ITEM rows when toggle is off (CATEGORY/SUBTOTAL always stay)
    if (!showUnpriced) {
      rows = rows.filter(r => {
        const type = r.item.rowType || 'ITEM'
        if (type === 'CATEGORY' || type === 'SUBTOTAL') return true
        return r.hasAnyPrice
      })
    }

    if (!paretoEnabled || !paretoClassification) {
      return { visibleRows: rows, trivialRows: [] as typeof rows }
    }
    return {
      visibleRows: rows.filter(r => {
        const type = r.item.rowType || 'ITEM'
        if (type !== 'ITEM') return true // structural rows always visible
        const cls = paretoClassification[r.item.id]
        return cls && (cls.abcClass === 'A' || cls.abcClass === 'B')
      }),
      trivialRows: rows.filter(r => {
        const type = r.item.rowType || 'ITEM'
        if (type !== 'ITEM') return false
        const cls = paretoClassification[r.item.id]
        return !cls || cls.abcClass === 'C'
      }),
    }
  }, [rowAnalysis, showUnpriced, paretoEnabled, paretoClassification])

  // Recompute totals from row data when fillAverages is on (always uses totalPrice, independent of priceMode)
  const computedTotals = useMemo(() => {
    if (!fillAverages) return bidTotals
    const totals: Record<string, number> = {}
    for (const bid of bids) totals[bid.bidId] = 0

    const renderedGroups = new Set<string>()
    for (const item of tenderItems) {
      const rowPrices: { bidId: string; price: number }[] = []

      for (const bid of bids) {
        const group = (bid.coverageGroups || []).find(
          (g: any) => (g.tenderItems || []).some((ti: any) => ti.id === item.id),
        )
        if (!group) continue
        if (group.groupType === 'MANY_TO_ONE' && renderedGroups.has(group.id)) continue
        if (group.groupType === 'MANY_TO_ONE') renderedGroups.add(group.id)
        const price = group.bidItems?.[0]?.totalPrice
        if (price != null) {
          totals[bid.bidId] += price
          rowPrices.push({ bidId: bid.bidId, price })
        }
      }

      if (rowPrices.length > 0) {
        const avg = rowPrices.reduce((s, p) => s + p.price, 0) / rowPrices.length
        const coveredBidIds = new Set(rowPrices.map(p => p.bidId))
        for (const bid of bids) {
          if (!coveredBidIds.has(bid.bidId)) totals[bid.bidId] += avg
        }
      }
    }
    // Bidder-added items beyond the BoQ have no tender row, but they are part
    // of the bid price — keep recomputed totals consistent with bid.totalPrice
    for (const bid of bids) {
      totals[bid.bidId] += bid.extraItemsTotal || 0
    }
    return totals
  }, [fillAverages, tenderItems, bids, bidTotals])

  const computedCheapestBidId = useMemo(() => {
    let min = Infinity
    let id = ''
    for (const [bidId, total] of Object.entries(computedTotals)) {
      if (total > 0 && total < min) { min = total; id = bidId }
    }
    return id
  }, [computedTotals])

  // Rank bids by total price (cheapest first)
  const bidRanks = useMemo(() => {
    const sorted = [...bids]
      .filter((b: any) => (computedTotals[b.bidId] || 0) > 0)
      .sort((a: any, b: any) => (computedTotals[a.bidId] || 0) - (computedTotals[b.bidId] || 0))
    const ranks: Record<string, number> = {}
    sorted.forEach((bid: any, i: number) => { ranks[bid.bidId] = i + 1 })
    return ranks
  }, [bids, computedTotals])

  const getDeviationClass = (price: number, avg: number | null) => {
    if (avg == null || avg === 0) return ''
    const deviation = (price - avg) / avg
    if (deviation > 0.10) return 'bg-red-50'
    if (deviation < -0.10) return 'bg-green-50'
    return ''
  }

  const getMatchQuality = (bid: any) => {
    const groups = bid.coverageGroups || []
    const high = groups.filter((g: any) => g.confidence === 'HIGH').length
    const medium = groups.filter((g: any) => g.confidence === 'MEDIUM').length
    const low = groups.filter((g: any) => g.confidence === 'LOW' || !g.confidence).length
    return { high, medium, low }
  }

  const colSpan = 4 + bids.length + (paretoEnabled ? 1 : 0)

  const toggleCategory = useCallback((itemId: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }, [])

  // Build parent group map: each ITEM row → its preceding CATEGORY/SUBTOTAL group ID
  const parentGroupMap = useMemo(() => {
    const map = new Map<string, string>() // itemId → groupId
    let currentGroupId: string | null = null
    for (const row of visibleRows) {
      const type = row.item.rowType || 'ITEM'
      if (type === 'CATEGORY' || type === 'SUBTOTAL') {
        currentGroupId = row.item.id
      } else if (currentGroupId) {
        map.set(row.item.id, currentGroupId)
      }
    }
    return map
  }, [visibleRows])

  // Count children per group
  const groupChildCount = useMemo(() => {
    const counts = new Map<string, number>()
    parentGroupMap.forEach((groupId) => {
      counts.set(groupId, (counts.get(groupId) || 0) + 1)
    })
    return counts
  }, [parentGroupMap])

  // Compute per-bid subtotals for each CATEGORY/SUBTOTAL group
  // Maps groupId → { [bidId]: sumOfItemPrices }
  const groupBidSubtotals = useMemo(() => {
    const totals = new Map<string, Record<string, number>>()
    for (const row of rowAnalysis) {
      const type = row.item.rowType || 'ITEM'
      if (type !== 'ITEM') continue
      const groupId = parentGroupMap.get(row.item.id)
      if (!groupId) continue
      if (!totals.has(groupId)) totals.set(groupId, {})
      const groupTotals = totals.get(groupId)!
      for (const [bidId, cell] of Object.entries(row.bidCells)) {
        if (cell.totalPrice != null) {
          groupTotals[bidId] = (groupTotals[bidId] || 0) + cell.totalPrice
        }
      }
    }
    return totals
  }, [rowAnalysis, parentGroupMap])

  // Shared row renderer
  const renderRow = (
    { item, bidCells, avgPrice, cheapestInRow }: typeof rowAnalysis[0],
    dimmed = false,
  ) => {
    const pareto = paretoClassification?.[item.id]
    const rowType = item.rowType || 'ITEM'
    const isGroup = rowType === 'CATEGORY' || rowType === 'SUBTOTAL'
    const childCount = isGroup ? groupChildCount.get(item.id) || 0 : 0
    const isCollapsed = isGroup && collapsedCategories.has(item.id)

    // Hide rows whose parent group is collapsed
    const parentId = parentGroupMap.get(item.id)
    if (parentId && collapsedCategories.has(parentId)) return null

    // CATEGORY rows render as collapsible full-width headers
    if (rowType === 'CATEGORY') {
      return (
        <TableRow
          key={item.id}
          className="cursor-pointer hover:bg-muted/40 bg-muted/30 font-medium"
          onClick={childCount > 0 ? () => toggleCategory(item.id) : undefined}
        >
          <TableCell colSpan={colSpan} className="py-1.5 px-4">
            <span className="flex items-center gap-1.5 text-sm">
              {childCount > 0 && (
                <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', isCollapsed && '-rotate-90')} />
              )}
              {item.code && (
                <Badge variant="outline" className="font-mono text-xs px-1.5 py-0">{item.code}</Badge>
              )}
              {item.description}
              {childCount > 0 && (
                <Badge variant="secondary" className="text-[10px] px-1 py-0 font-normal">{childCount}</Badge>
              )}
            </span>
          </TableCell>
        </TableRow>
      )
    }

    // SUBTOTAL rows show per-bid computed subtotals with deviation coloring
    if (rowType === 'SUBTOTAL') {
      const bidSubs = groupBidSubtotals.get(item.id) || {}
      const subPrices = Object.values(bidSubs).filter(v => v > 0)
      const subAvg = subPrices.length > 0 ? subPrices.reduce((a, b) => a + b, 0) / subPrices.length : null
      const subCheapestBidId = subPrices.length > 0
        ? Object.entries(bidSubs).reduce((min, [id, v]) => v > 0 && v < (min[1] || Infinity) ? [id, v] : min, ['', Infinity] as [string, number])[0]
        : null

      return (
        <TableRow
          key={item.id}
          className={cn(
            'bg-muted/20 font-medium border-t',
            childCount > 0 && 'cursor-pointer hover:bg-muted/40',
          )}
          onClick={childCount > 0 ? () => toggleCategory(item.id) : undefined}
        >
          <TableCell className="font-mono text-xs">
            {item.code && (
              <Badge variant="outline" className="font-mono text-xs px-1.5 py-0">{item.code}</Badge>
            )}
          </TableCell>
          <TableCell className="text-sm">
            <span className="flex items-center gap-1.5">
              {childCount > 0 && (
                <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', isCollapsed && '-rotate-90')} />
              )}
              {item.description}
              {childCount > 0 && (
                <Badge variant="secondary" className="text-[10px] px-1 py-0 font-normal">{childCount}</Badge>
              )}
            </span>
          </TableCell>
          <TableCell />
          <TableCell />
          {paretoEnabled && <TableCell />}
          {bids.map((bid: any) => {
            const bidTotal = bidSubs[bid.bidId]
            if (bidTotal == null) {
              return <TableCell key={bid.bidId} className="text-right text-muted-foreground">—</TableCell>
            }
            const isCheapest = bid.bidId === subCheapestBidId
            const devClass = getDeviationClass(bidTotal, subAvg)
            return (
              <TableCell
                key={bid.bidId}
                className={cn(
                  'text-right tabular-nums text-sm font-semibold',
                  devClass,
                  isCheapest && 'text-green-700',
                )}
              >
                {formatNumber(bidTotal)}
              </TableCell>
            )
          })}
        </TableRow>
      )
    }

    return (
      <TableRow
        key={item.id}
        className={cn(
          dimmed && 'text-muted-foreground',
          paretoEnabled && pareto && ABC_ROW_CLASSES[pareto.abcClass],
        )}
      >
        <TableCell className="font-mono text-xs">
          <div className="flex items-center gap-1">
            {paretoEnabled && pareto && (
              <Badge
                variant="outline"
                className={cn('text-[10px] px-1 py-0 font-bold', ABC_BADGE_CLASSES[pareto.abcClass])}
              >
                {pareto.abcClass}
              </Badge>
            )}
            {item.code ? (
              <Badge variant="outline" className={cn('font-mono text-xs px-1.5 py-0', dimmed && 'opacity-60')}>
                {item.code}
              </Badge>
            ) : '-'}
          </div>
        </TableCell>
        <TableCell className="text-sm">{item.description}</TableCell>
        <TableCell className="text-xs text-muted-foreground">{item.unit || '-'}</TableCell>
        <TableCell className="text-right tabular-nums text-sm">{formatNumber(item.quantity)}</TableCell>
        {paretoEnabled && (
          <TableCell className="text-right tabular-nums text-xs text-muted-foreground">
            {pareto ? `${pareto.cumulativePercent.toFixed(1)}%` : '-'}
          </TableCell>
        )}
        {bids.map((bid: any) => {
          const cell = bidCells[bid.bidId]
          if (!cell || cell.isIncluded || cell.price == null) {
            return <TableCell key={bid.bidId} className="text-right text-muted-foreground">—</TableCell>
          }
          if (cell.isFilled) {
            return (
              <TableCell key={bid.bidId} className="text-right tabular-nums text-sm">
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="italic text-muted-foreground border-b border-dashed border-muted-foreground/40 cursor-help">
                        {formatNumber(cell.price)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      {t('ai_filled_estimate')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
            )
          }
          const isCheapest = cheapestInRow === bid.bidId
          const devClass = getDeviationClass(cell.price, avgPrice)
          return (
            <TableCell
              key={bid.bidId}
              className={cn(
                'text-right tabular-nums text-sm',
                devClass,
                isCheapest && 'font-semibold text-green-700',
              )}
            >
              {formatNumber(cell.price)}
            </TableCell>
          )
        })}
      </TableRow>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        <LoaderCircle className="h-5 w-5 animate-spin mr-2" />
        {t('loading')}
      </div>
    )
  }

  if (!comparison) {
    return (
      <Card>
        <CardContent className="text-center text-muted-foreground p-8">
          {t('ai_no_comparison_data')}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Coverage summary cards */}
      {allBids.length > 0 && (
        <div ref={cardsRef} className="grid gap-4" style={{ gridTemplateColumns: `repeat(${allBids.length}, 1fr)` }}>
          {allBids.map((bid: any) => {
            const isHidden = hiddenBidIds.has(bid.bidId)
            const level = getCoverageLevel(bid.coveragePercent || 0)
            const quality = getMatchQuality(bid)
            const rank = bidRanks[bid.bidId]
            const bidTotal = computedTotals[bid.bidId] || bid.totalPrice || 0
            const coveragePct = Math.round(bid.coveragePercent || 0)
            const priceDiffPercent = tenderTotal > 0
              ? ((bidTotal - tenderTotal) / tenderTotal) * 100
              : null
            const isWinner = !isHidden && rank === 1
            const rankColors: Record<number, string> = {
              1: 'text-amber-500',
              2: 'text-gray-400',
              3: 'text-amber-700',
            }
            return (
              <Card key={bid.bidId} className={cn(
                'transition-all',
                isHidden && 'opacity-50',
                isWinner && 'ring-2 ring-green-200 shadow-sm',
              )}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      {!isHidden && rank && rank <= 3 && (
                        <div className={cn('flex items-center justify-center h-8 w-8 rounded-full bg-muted/60', rankColors[rank] || 'text-muted-foreground')}>
                          {rank === 1 ? (
                            <Medal className="h-5 w-5" />
                          ) : (
                            <span className="text-sm font-bold">#{rank}</span>
                          )}
                        </div>
                      )}
                      {!isHidden && rank && rank > 3 && (
                        <div className="flex items-center justify-center h-8 w-8 rounded-full bg-muted/40 text-muted-foreground">
                          <span className="text-sm font-medium">#{rank}</span>
                        </div>
                      )}
                      <div>
                        <span className="font-semibold text-sm">{bid.bidderName}</span>
                        <div className={cn('text-lg font-bold tabular-nums tracking-tight', fillAverages && 'italic')}>
                          {formatNumber(bidTotal)}
                          {priceDiffPercent != null && (
                            <span className={cn('text-xs font-medium ml-1.5', {
                              'text-green-600': priceDiffPercent < 0,
                              'text-red-500': priceDiffPercent > 0,
                              'text-muted-foreground': priceDiffPercent === 0,
                            })}>
                              {priceDiffPercent > 0 ? '+' : ''}{priceDiffPercent.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => toggleBid(bid.bidId)}
                      title={isHidden ? t('ai_show_bid') : t('ai_hide_bid')}
                    >
                      {isHidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </Button>
                  </div>

                  {/* Coverage bar */}
                  <div className="mb-2">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-muted-foreground">{t('ai_coverage')}</span>
                      <span className={cn('text-xs font-medium', {
                        'text-green-600': coveragePct >= 80,
                        'text-amber-600': coveragePct >= 50 && coveragePct < 80,
                        'text-red-600': coveragePct < 50,
                      })}>{coveragePct}%</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className={cn('h-full rounded-full transition-all', {
                          'bg-green-500': coveragePct >= 80,
                          'bg-amber-500': coveragePct >= 50 && coveragePct < 80,
                          'bg-red-500': coveragePct < 50,
                        })}
                        style={{ width: `${Math.min(coveragePct, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Match quality dots */}
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span>{t('ai_match_quality')}:</span>
                    {quality.high > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-green-600">
                        <span className="h-1.5 w-1.5 rounded-full bg-green-500" />{quality.high}
                      </span>
                    )}
                    {quality.medium > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-amber-600">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />{quality.medium}
                      </span>
                    )}
                    {quality.low > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-red-600">
                        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />{quality.low}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Toggle + Price matrix */}
      <div className="rounded-md border border-border overflow-clip bg-card">
        {/* Toggle bar */}
        <div className="flex items-center justify-end gap-4 px-4 py-2 border-b">
          <div className="flex items-center gap-2">
            <Switch
              id="pareto-mode"
              checked={paretoEnabled}
              onCheckedChange={setParetoEnabled}
            />
            <label htmlFor="pareto-mode" className="text-xs text-muted-foreground cursor-pointer select-none">
              {t('ai_pareto_mode')}
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="fill-averages"
              checked={fillAverages}
              onCheckedChange={setFillAverages}
            />
            <label htmlFor="fill-averages" className="text-xs text-muted-foreground cursor-pointer select-none">
              {t('ai_fill_averages')}
            </label>
          </div>
          {unpricedItemCount > 0 && (
            <div className="flex items-center gap-2">
              <Switch
                id="show-unpriced"
                checked={showUnpriced}
                onCheckedChange={setShowUnpriced}
              />
              <label htmlFor="show-unpriced" className="text-xs text-muted-foreground cursor-pointer select-none">
                {t('ai_show_unpriced', 'Unpriced')} ({unpricedItemCount})
              </label>
            </div>
          )}
          <div className="inline-flex h-8 items-center rounded-md bg-muted p-0.5 text-muted-foreground">
            <button
              onClick={() => setPriceMode('unit')}
              className={cn(
                'inline-flex items-center justify-center rounded-sm px-3 py-1 text-xs font-medium transition-colors',
                priceMode === 'unit'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'hover:text-foreground',
              )}
            >
              {t('ai_unit_price_view')}
            </button>
            <button
              onClick={() => setPriceMode('total')}
              className={cn(
                'inline-flex items-center justify-center rounded-sm px-3 py-1 text-xs font-medium transition-colors',
                priceMode === 'total'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'hover:text-foreground',
              )}
            >
              {t('ai_total_price_view')}
            </button>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[90px]">{t('ai_item_code')}</TableHead>
              <TableHead>{t('ai_item_description')}</TableHead>
              <TableHead className="w-[50px]">{t('ai_item_unit')}</TableHead>
              <TableHead className="w-[80px] text-right">{t('ai_item_qty')}</TableHead>
              {paretoEnabled && (
                <TableHead className="w-[65px] text-right">{t('ai_pareto_cumulative')}</TableHead>
              )}
              {bids.map((bid: any) => (
                <TableHead key={bid.bidId} className="text-right" style={{ minWidth: 120 }}>
                  {bid.bidderName}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rowAnalysis.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="h-24 text-center">
                  {t('ai_no_items_found')}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {visibleRows.map((row) => renderRow(row))}

                {/* Pareto C-class collapsible */}
                {paretoEnabled && trivialRows.length > 0 && (
                  <>
                    <TableRow
                      className="cursor-pointer hover:bg-muted/30"
                      onClick={() => setShowTrivialItems(!showTrivialItems)}
                    >
                      <TableCell
                        colSpan={colSpan}
                        className="bg-muted/50 text-xs text-muted-foreground font-medium py-1.5 px-4"
                      >
                        <span className="flex items-center gap-1">
                          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showTrivialItems && 'rotate-180')} />
                          {t('ai_pareto_trivial_items', { count: trivialRows.length })}
                        </span>
                      </TableCell>
                    </TableRow>
                    {showTrivialItems && trivialRows.map((row) => renderRow(row, true))}
                  </>
                )}

              </>
            )}
          </TableBody>

          {/* Footer totals */}
          {tenderItems.length > 0 && (
            <tfoot ref={tfootRef} className="border-t-2 border-border">
              <tr className="font-semibold text-sm">
                <TableCell colSpan={4 + (paretoEnabled ? 1 : 0)}>{t('ai_total')}</TableCell>
                {bids.map((bid: any) => {
                  const total = computedTotals[bid.bidId] || 0
                  const isCheapest = bid.bidId === computedCheapestBidId
                  return (
                    <TableCell
                      key={bid.bidId}
                      className={cn(
                        'text-right tabular-nums',
                        isCheapest && 'text-green-700',
                        fillAverages && 'italic',
                      )}
                    >
                      {formatNumber(total)}
                    </TableCell>
                  )
                })}
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {/* Bidder-added items beyond the BoQ */}
      {bids.some((bid: any) => (bid.extraItems || []).length > 0) && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2">
            <PackagePlus className="h-4 w-4 text-amber-600" />
            <h4 className="text-sm font-semibold">{t('ai_extra_items_heading')}</h4>
          </div>
          <p className="text-xs text-muted-foreground">{t('ai_extra_items_hint')}</p>
          {bids
            .filter((bid: any) => (bid.extraItems || []).length > 0)
            .map((bid: any) => (
              <div key={bid.bidId} className="rounded-md border border-amber-200 overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-amber-50/60 border-b border-amber-200/60">
                  <span className="text-sm font-medium">{bid.bidderName}</span>
                  <span className="text-sm font-semibold tabular-nums">
                    {formatNumber(bid.extraItemsTotal)}
                  </span>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[110px]">{t('ai_item_code')}</TableHead>
                      <TableHead>{t('ai_item_description')}</TableHead>
                      <TableHead className="w-[60px]">{t('ai_item_unit')}</TableHead>
                      <TableHead className="w-[80px] text-right">{t('ai_item_qty')}</TableHead>
                      <TableHead className="w-[110px] text-right">{t('ai_item_unit_price')}</TableHead>
                      <TableHead className="w-[120px] text-right">{t('ai_item_total_price')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bid.extraItems.map((item: any) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-mono text-xs">{item.code || '-'}</TableCell>
                        <TableCell className="text-sm whitespace-pre-line">{item.description}</TableCell>
                        <TableCell className="text-sm">{item.unit || '-'}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {item.quantity != null ? formatNumber(item.quantity) : '-'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatNumber(item.unitPrice)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          {formatNumber(item.totalPrice)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
        </div>
      )}

      {/* Footer row count */}
      <div className="flex items-center justify-end py-1 px-1">
        <span className="text-xs text-muted-foreground">
          {paretoEnabled && paretoClassification ? (
            <>
              {Object.values(paretoClassification).filter(p => p.abcClass === 'A').length} A
              {' / '}
              {Object.values(paretoClassification).filter(p => p.abcClass === 'B').length} B
              {' / '}
              {Object.values(paretoClassification).filter(p => p.abcClass === 'C').length} C
              {' — '}
              {tenderItems.length} {t('Grid_row_count', 'row(s)')}
            </>
          ) : (
            <>{tenderItems.length} {t('Grid_row_count', 'row(s)')}</>
          )}
        </span>
      </div>

      {/* Fixed bottom bar — aligned with table columns */}
      {!cardsVisible && bids.length > 0 && colPositions.length > 1 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-card/95 backdrop-blur-sm shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
          <div className="relative h-10">
            {/* "Total" label — aligned with tfoot's first cell */}
            <div
              className="absolute top-1/2 -translate-y-1/2 text-sm font-semibold px-4"
              style={{ left: colPositions[0].left, width: colPositions[0].width }}
            >
              {t('ai_total')}
            </div>
            {/* Bid prices — aligned with tfoot's bid cells */}
            {bids.map((bid: any, i: number) => {
              const pos = colPositions[i + 1]
              if (!pos) return null
              const total = computedTotals[bid.bidId] || 0
              const isCheapest = bid.bidId === computedCheapestBidId
              return (
                <div
                  key={bid.bidId}
                  className={cn(
                    'absolute top-1/2 -translate-y-1/2 text-right text-sm font-semibold tabular-nums px-4',
                    isCheapest && 'text-green-700',
                    fillAverages && 'italic',
                  )}
                  style={{ left: pos.left, width: pos.width }}
                >
                  {formatNumber(total)}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default AiBidComparison
